import { useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Input } from "@agentnative/ui";
import { AuthLayout, authInputClass, authButtonClass, authLinkClass, authLinkRowClass } from "../components/AuthLayout";
import { requestPasswordReset, fetchCsrfToken } from "../lib/api/auth";
import { apiErrorMessage } from "../lib/api/client";

// Step 1 of a password reset: ask for the email. The server answers the same
// way whether or not the address has an account (so this page cannot be used
// to find out who is registered), and this page mirrors that: after a submit
// it always shows the same "if an account exists" message.
export function ForgotPasswordRoute() {
  const [email, setEmail] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      await fetchCsrfToken();
      return requestPasswordReset({ email: email.trim() });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We will email you a link to choose a new one">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {mutation.isError && (
          <Alert variant="danger" title="Something went wrong">
            {apiErrorMessage(mutation.error)}
          </Alert>
        )}
        {mutation.isSuccess && (
          <Alert variant="success" title="Check your email">
            If an account exists for that address, we have sent a link to reset the password. The link
            expires in 24 hours. If it does not arrive, check your spam folder.
          </Alert>
        )}
        <Input
          className={authInputClass}
          label="Email"
          id="forgot-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />
        <Button type="submit" variant="primary" className={authButtonClass} disabled={mutation.isPending || !email.trim()}>
          {mutation.isPending ? "Sending..." : "Send reset link"}
        </Button>
        <div className={authLinkRowClass}>
          <a href="/login" className={authLinkClass}>
            Back to sign in
          </a>
        </div>
      </form>
    </AuthLayout>
  );
}
